import React, { FC, useEffect, useRef } from 'react'
import { Flex, Text, Box, IconButton, Tooltip, Link } from '@chakra-ui/react'
import { useUserContext } from '../../contexts/UserContext'
import { WEBAPP_LOCAL_STORAGE_KEYS } from '../../models/browserStorage.types'
import { useLocalStorageBoolean } from '../../utils/genericHooks'
import { MdClose } from 'react-icons/md'
import { DateTime } from 'luxon'

interface Props {
  children: React.ReactNode
}

const BANNER_END_DATE = '2026-03-27'
const DOCS_URL =
  'https://docs.skipper.co/Changes-to-Skipper-s-free-plan-Basic-3205d2243c8a80908c4cd67c9a9a03e0'

const FreePlanChangingBannerWrapper: FC<Props> = (props) => {
  const { children } = props
  const { captureAnalytics, userInfo, isUserInfoLoading } = useUserContext()
  const hasTrackedDisplay = useRef<boolean>(false)

  const isPastEndDate = DateTime.now() >= DateTime.fromISO(BANNER_END_DATE)

  const [isDismissed, setIsDismissed] = useLocalStorageBoolean(
    WEBAPP_LOCAL_STORAGE_KEYS.IsFreePlanChangingBannerDismissed,
    null,
  )

  const shouldShow =
    !isUserInfoLoading && !userInfo?.is_premium && !isPastEndDate && isDismissed !== false

  useEffect(() => {
    if (!userInfo || !shouldShow || hasTrackedDisplay.current) {
      return
    }

    captureAnalytics('free_plan_changing_banner:displayed')
    hasTrackedDisplay.current = true
  }, [userInfo, shouldShow, captureAnalytics])

  if (!shouldShow) {
    return children
  }

  return (
    <Flex h="100%" w="100%" flexDirection="column">
      <Flex
        className="is-ellipsis-1"
        w="100%"
        h="40px"
        alignItems="center"
        justifyContent="center"
        color="white"
        bg="#F59E0B"
        p="8px"
      >
        <Text fontSize="14px" fontWeight={500} lineHeight="22px">
          Skipper&apos;s free plan is changing on March 27.{' '}
          <Link
            href={DOCS_URL}
            isExternal
            textDecoration="underline"
            _hover={{ color: '#78350F', textDecoration: 'underline' }}
            onClick={() => {
              captureAnalytics('free_plan_changing_banner:link_click')
            }}
          >
            Here&apos;s what to know &rarr;
          </Link>
        </Text>

        <Tooltip placement="bottom" label="Dismiss">
          <IconButton
            variant="outline"
            border="none"
            position="absolute"
            right="8px"
            aria-label="Dismiss banner"
            as="a"
            icon={<MdClose color="white" />}
            onClick={() => {
              captureAnalytics('free_plan_changing_banner:dismiss_click')
              setIsDismissed(false)
            }}
            _hover={{ bg: '#ebebeb4b' }}
            height="24px"
            width="24px"
            minWidth="24px"
          />
        </Tooltip>
      </Flex>
      <Box h="calc(100% - 40px)" w="100%">
        {children}
      </Box>
    </Flex>
  )
}

export default FreePlanChangingBannerWrapper
